import { useCallback, useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';

/** The app's `toast()`, same props and same returned handle. */
export type ScopedToast = typeof toast;

/**
 * `toast()` whose success and info toasts close when the caller unmounts (the
 * code's keep-the-handle pattern, ClientBlankSupplyGrant): a note about a page
 * you left is noise. Error toasts (`variant: 'destructive'`) are never
 * tracked: they stay until dismissed (DESIGN.md §15.5, no exception). Global
 * toast behaviour is unchanged. Stable: safe to pass to memoised children.
 *
 * - A success or info toast raised once the caller is gone (a request that
 *   settled after the user left) closes at once.
 * - `alive` starts true: a child's mount effect runs before ours, and its
 *   toast belongs to this page too.
 * - The close on unmount waits one microtask. StrictMode (dev) runs every
 *   mount effect's cleanup and setup once more, back to back, and a toast a
 *   mount effect raised (Client prices' hint when its grid is already cached)
 *   must survive that rehearsal; the second setup cancels the close.
 *
 * Consumers: the 3PL usage tab (the bill's add and remove toasts) and
 * Products > Client prices (the hint a bill's Set price lands with).
 */
export function useScopedToast(): ScopedToast {
  const handles = useRef(new Set<ReturnType<typeof toast>>());
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    const open = handles.current;
    return () => {
      alive.current = false;
      queueMicrotask(() => {
        if (alive.current) return;
        open.forEach((handle) => handle.dismiss());
        open.clear();
      });
    };
  }, []);
  return useCallback<ScopedToast>((props) => {
    const handle = toast(props);
    if (props.variant === 'destructive') return handle;
    if (alive.current) handles.current.add(handle);
    else handle.dismiss();
    return handle;
  }, []);
}
